import React, { useState } from 'react';
import { DollarSign, Plus, Check, X, Trash2 } from 'lucide-react';
import { useServices } from '../hooks/useServices';
import ProductsTabs from '../components/ProductsTabs';

export default function ServicesPage() {
  const { 
    services, 
    activeBusiness, 
    isProcessing,
    addService,
    updateService,
    deleteService,
    toggleServiceStatus
  } = useServices();

  const [newService, setNewService] = useState({ 
    nombre: "", 
    precio: "", 
    precio_cotizar: false,
    duracion_minutos: "30", 
    activo: true 
  });
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editServiceName, setEditServiceName] = useState('');
  const [editServicePrice, setEditServicePrice] = useState('');
  const [editServiceCotizar, setEditServiceCotizar] = useState(false);

  if (!activeBusiness) {
    return <div>Cargando...</div>;
  }

  const isRestaurant = activeBusiness.tipo_negocio === 'Restaurante';

  const handleAddService = () => {
    addService(newService, () => {
      setNewService({ 
        nombre: "", 
        precio: "", 
        precio_cotizar: false,
        duracion_minutos: "30", 
        activo: true 
      });
    });
  };

  const handleStartEditService = (service) => {
    const isQuote = service.precio === null || service.precio === undefined || Number(service.precio) === 0 || service.precio_cotizar;
    setEditingServiceId(service.id);
    setEditServiceName(service.nombre || '');
    setEditServicePrice(isQuote ? '' : (service.precio ?? ''));
    setEditServiceCotizar(Boolean(isQuote));
  };

  const handleSaveEditService = (serviceId) => {
    const newPrice = editServiceCotizar ? null : (parseFloat(editServicePrice) || 0);
    updateService(serviceId, { 
      nombre: editServiceName, 
      precio: newPrice,
      precio_cotizar: editServiceCotizar 
    }, () => {
      setEditingServiceId(null);
    });
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <ProductsTabs />
      <div>
        <h2 className="text-xl font-extrabold text-seryi-heading">
          {isRestaurant ? "Menú y Carta Digital (Productos)" : "Catálogo de Servicios"}
        </h2>
        <p className="text-xs text-seryi-text">
          {isRestaurant 
            ? "Define tus platillos, bebidas y precios para que Keyla IA los ofrezca en WhatsApp y tome pedidos." 
            : "Define los precios o márcalos como 'A cotizar' y duraciones para que Keyla IA sepa qué ofrecer al agendar."}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Creador de Servicios / Productos */}
        <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-4 self-start shadow-xl">
          <h3 className="font-bold text-xs uppercase text-seryi-heading tracking-wider flex items-center justify-between">
            <span>{isRestaurant ? "Nuevo Producto / Platillo" : "Nuevo Servicio"}</span>
            <span className="text-[10px] text-seryi-green font-bold">Activo</span>
          </h3>
          
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-[10px] uppercase font-bold text-seryi-text">Nombre del Servicio / Platillo</label>
              <input 
                type="text" 
                className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3.5 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
                placeholder={isRestaurant ? "Ej: Hamburguesa Artesanal" : "Ej: Asesoría / Corte Especial"}
                value={newService.nombre}
                onChange={(e) => setNewService(prev => ({ ...prev, nombre: e.target.value }))}
              />
            </div>

            {/* Checkbox Precio a Cotizar / Opcional */}
            <div className="p-3 bg-seryi-bg-dark/80 border border-seryi-bg-light rounded-xl flex items-center justify-between">
              <div className="space-y-0.5 pr-2">
                <label htmlFor="chk-cotizar" className="text-xs font-bold text-seryi-heading cursor-pointer block">
                  Precio a cotizar / Opcional
                </label>
                <p className="text-[10px] text-seryi-text">Muestra "A cotizar" o "Consultar" en vez de tarifa fija.</p>
              </div>
              <input 
                id="chk-cotizar"
                type="checkbox"
                checked={newService.precio_cotizar}
                onChange={(e) => setNewService(prev => ({ 
                  ...prev, 
                  precio_cotizar: e.target.checked,
                  precio: e.target.checked ? "" : prev.precio 
                }))}
                className="w-4 h-4 rounded text-seryi-green focus:ring-seryi-green border-seryi-bg-light cursor-pointer"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-seryi-text">
                  {newService.precio_cotizar ? "Precio (A Cotizar)" : "Precio ($)"}
                </label>
                <input 
                  type="number" 
                  disabled={newService.precio_cotizar}
                  className={`w-full rounded-xl px-3 py-2 text-xs font-bold focus:outline-none transition-all ${
                    newService.precio_cotizar
                      ? 'bg-slate-800/40 border border-slate-700 text-slate-500 cursor-not-allowed italic'
                      : 'bg-seryi-bg-dark border border-emerald-500/50 text-emerald-400 focus:border-seryi-green'
                  }`}
                  placeholder={newService.precio_cotizar ? "A cotizar" : "18900"}
                  value={newService.precio_cotizar ? "" : newService.precio}
                  onChange={(e) => setNewService(prev => ({ ...prev, precio: parseFloat(e.target.value) || "" }))}
                />
              </div>
              {!isRestaurant && (
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold text-seryi-text">Duración (Min)</label>
                  <input 
                    type="number" 
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
                    placeholder="30"
                    value={newService.duracion_minutos}
                    onChange={(e) => setNewService(prev => ({ ...prev, duracion_minutos: e.target.value }))}
                  />
                </div>
              )}
            </div>

            <button 
              onClick={handleAddService}
              disabled={isProcessing}
              className="w-full bg-seryi-green text-seryi-bg-dark hover:brightness-110 font-extrabold py-3 rounded-xl text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-lg shadow-seryi-green/20 active:scale-95 disabled:opacity-50 mt-2"
            >
              <Plus size={15} /> {isRestaurant ? "Añadir al Menú" : "Añadir al Catálogo"}
            </button>
          </div>
        </div>

        {/* Listado de Servicios / Productos */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-xs uppercase text-seryi-heading tracking-wider">
              {isRestaurant ? `Platillos en el Menú (${services.length})` : `Servicios Activos (${services.length})`}
            </h3>
            <span className="text-[10px] text-seryi-text font-mono">Precios en vivo</span>
          </div>

          {services.length === 0 ? (
            <div className="bg-seryi-bg border border-seryi-bg-light p-8 rounded-3xl text-center space-y-2">
              <p className="text-xs text-seryi-text">
                {isRestaurant ? "No hay productos registrados en el menú aún." : "No hay servicios registrados en este negocio aún."}
              </p>
            </div>
          ) : (
            services.map(service => {
              const isQuote = service.precio === null || service.precio === undefined || Number(service.precio) === 0 || service.precio_cotizar;
              
              return (
                <div key={service.id} className="bg-seryi-bg border border-seryi-bg-light p-4.5 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-seryi-green/30 transition-all shadow-sm">
                  {editingServiceId === service.id ? (
                    <div className="flex-1 space-y-3">
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <input 
                          type="text"
                          className="flex-1 bg-seryi-bg-dark border border-indigo-500 rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
                          value={editServiceName}
                          onChange={(e) => setEditServiceName(e.target.value)}
                          placeholder="Nombre del servicio"
                        />
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-emerald-400 font-bold">$</span>
                          <input 
                            type="number"
                            disabled={editServiceCotizar}
                            className={`w-28 rounded-xl px-3 py-2 text-xs font-bold focus:outline-none ${
                              editServiceCotizar 
                                ? 'bg-slate-800 border border-slate-700 text-slate-500 italic' 
                                : 'bg-seryi-bg-dark border border-emerald-500 text-emerald-400'
                            }`}
                            value={editServiceCotizar ? "" : editServicePrice}
                            onChange={(e) => setEditServicePrice(e.target.value)}
                            placeholder={editServiceCotizar ? "A cotizar" : "Precio"}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-1">
                        <label className="flex items-center gap-2 text-xs text-seryi-heading cursor-pointer">
                          <input 
                            type="checkbox"
                            checked={editServiceCotizar}
                            onChange={(e) => setEditServiceCotizar(e.target.checked)}
                            className="rounded text-seryi-green focus:ring-seryi-green cursor-pointer"
                          />
                          <span>Marcar como "A cotizar / Opcional"</span>
                        </label>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleSaveEditService(service.id)}
                            disabled={isProcessing}
                            className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold transition-all shadow-md disabled:opacity-50 flex items-center gap-1"
                          >
                            <Check size={13} />
                            <span>Guardar</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingServiceId(null)}
                            className="px-3 py-1.5 bg-seryi-bg-light hover:bg-slate-700 text-seryi-heading rounded-xl text-xs font-bold transition-all"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center font-bold text-emerald-400 border border-emerald-500/20 shrink-0">
                          <DollarSign size={16} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-sm text-seryi-heading">{service.nombre}</p>
                            {isQuote && (
                              <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/30">
                                A Cotizar
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            {!isRestaurant && service.duracion_minutos && (
                              <span className="text-xs text-seryi-text">{service.duracion_minutos} min</span>
                            )}
                            {isQuote ? (
                              <span className="text-xs font-bold text-amber-400">Consultar precio</span>
                            ) : (
                              <span className="text-xs text-emerald-400 font-extrabold">
                                ${Number(service.precio || 0).toLocaleString('es-CO')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button 
                          type="button"
                          onClick={() => handleStartEditService(service)}
                          className="px-3 py-1.5 bg-seryi-green/10 hover:bg-seryi-green/20 text-seryi-green border border-seryi-green/30 rounded-xl text-xs font-bold transition-all"
                        >
                          ✏️ Editar
                        </button>
                        <button 
                          onClick={() => toggleServiceStatus(service.id)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all ${
                            service.activo 
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                              : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                          }`}
                        >
                          {service.activo ? <Check size={12} /> : <X size={12} />}
                          <span>{service.activo ? "Activo" : "Inactivo"}</span>
                        </button>
                        <button 
                          onClick={() => deleteService(service.id)}
                          className="p-2 text-seryi-text hover:text-rose-400 hover:bg-rose-500/10 rounded-xl transition-all"
                          title="Eliminar servicio"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
